import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'result',
    'resultWrapper',
    'resultDisplay',
    'resultText',
    'resultCellA',
    'submitBtn',
    'resetBtn',
    'saveButtonCell',
    'sideA',
    'sideB',
    'sideAWrapper',
    'sideBWrapper',
    'sideAText',
    'sideBText',
    'gameHeadersRow',
    'gameScoreInput',
    'gameScoreWrapper',
    'gameScoreCell',
    'gameHeaderCell',
  ];
  static values = { valid: Boolean, maxGames: Number, defaultWinnerScore: { type: Number, default: 11 }, defaultLoserScore: { type: Number, default: 7 } };

  initialize() {
    // Store initial values for player selects to use when resetting
    if (this.hasSideATarget && this.hasSideBTarget) {
      this.initialSideAValue = this.sideATarget.value;
      this.initialSideBValue = this.sideBTarget.value;
    }
  }

  connect() {
    // Store initial values if not already set
    if (!this.initialSideAValue && this.hasSideATarget) {
      this.initialSideAValue = this.sideATarget.value;
    }
    if (!this.initialSideBValue && this.hasSideBTarget) {
      this.initialSideBValue = this.sideBTarget.value;
    }
    
    this.updateValid();
    this.updateResultOptions();
  }

  playerChanged(event) {
    this.updateValid();
    this.updateResultOptions();
    this.clearGameScores();

    // Ensure dropdowns are visible and static text is hidden
    this.sideAWrapperTarget.classList.remove('d-none');
    this.sideBWrapperTarget.classList.remove('d-none');
    this.sideATextTarget.classList.add('d-none');
    this.sideBTextTarget.classList.add('d-none');
  }

  resultChanged(event) {
    // First clear any existing game scores
    this.clearGameScores();

    // Only show game scores when we have a valid result selected
    if (this.resultTarget.value) {
      this.showResultDisplay();
      this.showPlayerNames();
      this.addGameScores();
      // Update validation after adding game scores
      this.updateValid();
    } else {
      this.updateValid();
    }
  }

  showPlayerNames() {
    // Get the selected player names from the dropdowns
    const sideAName = this.getSelectedNameTextFromTarget(this.sideATarget);
    const sideBName = this.getSelectedNameTextFromTarget(this.sideBTarget);

    // Set the text content for the static player name displays
    this.sideATextTarget.textContent = sideAName;
    this.sideBTextTarget.textContent = sideBName;

    // Hide the dropdowns
    this.sideAWrapperTarget.classList.add('d-none');
    this.sideBWrapperTarget.classList.add('d-none');

    // Show the static text
    this.sideATextTarget.classList.remove('d-none');
    this.sideBTextTarget.classList.remove('d-none');
  }

  resetForm(event) {
    event.preventDefault();

    // First clear any game score cells
    this.clearGameScores();

    // Reset player selections to initial values
    this.sideATarget.value = this.initialSideAValue;
    this.sideBTarget.value = this.initialSideBValue;

    // Reset result dropdown to first option (unselected)
    this.resultTarget.selectedIndex = 0;

    // Make sure result column header is visible
    const headerRow = this.gameHeadersRowTarget;
    const headerCells = headerRow.getElementsByTagName('th');
    if (headerCells.length >= 3) {
      headerCells[2].style.display = '';
    }

    // Make result column cell visible - only in this controller's scope
    this.resultCellATarget.style.display = '';

    // Make sure result dropdown is visible (not the display text)
    this.resultWrapperTarget.classList.remove('d-none');
    this.resultDisplayTarget.classList.add('d-none');

    // Show dropdowns, hide static text
    this.sideAWrapperTarget.classList.remove('d-none');
    this.sideBWrapperTarget.classList.remove('d-none');
    this.sideATextTarget.classList.add('d-none');
    this.sideBTextTarget.classList.add('d-none');

    // Rebuild result options based on player selections
    this.handlePlayerSelection();

    // Hide action buttons
    this.resetBtnTarget.classList.add('d-none');
    this.submitBtnTarget.classList.add('d-none');

    // Update validation state last
    this.updateValid();
  }

  private;

  validValueChanged() {
    this.toggleActionButtons();
  }

  toggleActionButtons() {
    if (this.validValue) {
      this.submitBtnTarget.classList.remove('d-none');
      this.resetBtnTarget.classList.remove('d-none');
    } else {
      this.submitBtnTarget.classList.add('d-none');
      this.resetBtnTarget.classList.add('d-none');
    }
    this.submitBtnTarget.disabled = !this.validValue;
  }

  updateValid() {
    // both sides selected and a result
    if (
      this.sideATarget.value &&
      this.sideBTarget.value &&
      this.resultTarget.value
    ) {
      this.validValue = true;
      return;
    }

    // a forfeit/ineligible sub and b selected (no score)
    if ((this.sideATarget.value === '-1' || this.sideATarget.value === '-2') && this.sideBTarget.value > 0) {
      this.validValue = true;
      return;
    }

    // b forfeit/ineligible sub and a selected (no score)
    if ((this.sideBTarget.value === '-1' || this.sideBTarget.value === '-2') && this.sideATarget.value > 0) {
      this.validValue = true;
      return;
    }

    this.validValue = false;
  }

  updateResultOptions() {
    // One or more missing selections. Don't show result select
    if (!this.sideATarget.value || !this.sideBTarget.value) {
      this.hideResult();
      return;
    }

    // A forfeit or ineligible sub selected. Don't show result select
    if (['-1', '-2'].includes(this.sideATarget.value) || ['-1', '-2'].includes(this.sideBTarget.value)) {
      this.hideResult();
      return;
    }

    // @TODO handle same side selected

    // Both sides selected - update result options and show the dropdown
    this.handlePlayerSelection();

    // Make sure the result column is visible
    // Show the result column cell - only within this controller's scope
    this.resultCellATarget.style.display = '';

    // Show the result dropdown
    this.showResult();
  }

  showResult() {
    this.resultWrapperTarget.classList.remove('d-none');
    this.resultDisplayTarget.classList.add('d-none');
  }

  hideResult() {
    // Hide the result dropdown
    this.resultWrapperTarget.classList.add('d-none');
    this.resultDisplayTarget.classList.add('d-none');

    // Clear any game scores that might be visible
    this.clearGameScores();
  }

  showResultDisplay() {
    // Hide the dropdown completely - we won't show text in the column
    this.resultWrapperTarget.classList.add('d-none');
    this.resultDisplayTarget.classList.add('d-none');

    // Hide the Result column cell - only in this controller's scope
    this.resultCellATarget.style.display = 'none';

    // Also hide the Result header to maintain column alignment
    const headerRow = this.gameHeadersRowTarget;
    const headerCells = headerRow.getElementsByTagName('th');
    if (headerCells.length >= 3) {
      headerCells[2].style.display = 'none';
    }
  }

  hideResultDisplay() {
    this.resultDisplayTarget.classList.add('d-none');
  }

  // This method completely clears all game score inputs
  // Used when resetting the form to initial state
  clearGameScores() {
    // Remove all game header cells using their specific target
    if (this.hasGameHeaderCellTarget) {
      // Create a copy of the array to avoid issues with the live NodeList
      const headerCellsToRemove = [...this.gameHeaderCellTargets];
      headerCellsToRemove.forEach((cell) => cell.remove());
    }

    // Remove all game score cells using their specific target
    if (this.hasGameScoreCellTarget) {
      // Create a copy of the array to avoid issues with the live NodeList
      const scoreCellsToRemove = [...this.gameScoreCellTargets];
      scoreCellsToRemove.forEach((cell) => cell.remove());
    }
  }

  // Just an alias for clearGameScores now
  removeGameColumns() {
    this.clearGameScores();
  }

  addGameScores() {
    // Important: Clear any existing game score columns BEFORE checking result
    // At this point, the Result column might be hidden but we still want to preserve 3 columns

    // First remove any existing game score columns beyond the basic structure
    this.removeGameColumns();

    // Get the selected result to determine game count
    const result = this.resultTarget.value;
    if (!result) return;

    // Result is in the format "A3" not "A_3"
    const winner = result.charAt(0);
    const totalGames = parseInt(result.substring(1));

    // Get player rows within this controller's scope
    const sideARow = this.element.querySelector('.side-a-row');
    const sideBRow = this.element.querySelector('.side-b-row');

    if (!sideARow || !sideBRow) return;

    // Calculate games won by each side
    // Use maxGames value if available, default to 5 (best of 5)
    const maxGames = this.hasMaxGamesValue ? this.maxGamesValue : 5;
    const winnerGames = Math.ceil((maxGames + 1) / 2); // Games needed to win
    const loserGames = totalGames - winnerGames;

    // Create an array representing which side wins each game
    // Starting with the loser's games, then the winner's games
    const gameResults = [];
    const loserSide = winner === 'A' ? 'B' : 'A';

    // Add loser's games first
    for (let i = 0; i < loserGames; i++) {
      gameResults.push(loserSide);
    }

    // Add winner's games next
    for (let i = 0; i < winnerGames; i++) {
      gameResults.push(winner);
    }

    // Create game number headers
    const headerRow = this.gameHeadersRowTarget;
    for (let i = 0; i < totalGames; i++) {
      const gameNumber = i + 1;
      const gameHeader = document.createElement('th');
      gameHeader.className = 'text-center border-bottom game-header'; // Adding bottom border but no side borders
      gameHeader.setAttribute('data-game-number', gameNumber);
      gameHeader.setAttribute('data-match-score-target', 'gameHeaderCell');
      gameHeader.textContent = `Game ${gameNumber}`;
      headerRow.appendChild(gameHeader);
    }

    // Add score input cells for each game
    for (let i = 0; i < totalGames; i++) {
      const gameNumber = i + 1;
      const gameWinner = gameResults[i]; // Who wins this game

      // Default scores based on sport configuration
      const winnerScore = this.defaultWinnerScoreValue;
      const loserScore = this.defaultLoserScoreValue;
      let sideAScore, sideBScore;
      if (gameWinner === 'A') {
        sideAScore = winnerScore;
        sideBScore = loserScore;
      } else {
        sideAScore = loserScore;
        sideBScore = winnerScore;
      }

      // Side A score input cell
      const sideACell = document.createElement('td');
      sideACell.className = 'border-0 game-score-cell'; // Remove border
      sideACell.setAttribute('data-match-score-target', 'gameScoreCell');
      sideACell.setAttribute('data-game-number', gameNumber);
      sideACell.setAttribute('data-side', 'a');
      const sideAInput = this.createScoreInput(gameNumber, 'a', sideAScore);
      sideACell.appendChild(sideAInput);
      sideARow.appendChild(sideACell);

      // Side B score input cell
      const sideBCell = document.createElement('td');
      sideBCell.className = 'border-0 game-score-cell'; // Remove border
      sideBCell.setAttribute('data-match-score-target', 'gameScoreCell');
      sideBCell.setAttribute('data-game-number', gameNumber);
      sideBCell.setAttribute('data-side', 'b');
      const sideBInput = this.createScoreInput(gameNumber, 'b', sideBScore);
      sideBCell.appendChild(sideBInput);
      sideBRow.appendChild(sideBCell);
    }
  }

  createScoreInput(gameNumber, side, defaultValue = null) {
    const input = document.createElement('input');
    input.type = 'number';
    input.name = `match[games_attributes][${
      gameNumber - 1
    }][side_${side}_score]`;
    input.className = 'form-control text-center';
    input.min = '0';
    input.max = '99';
    input.placeholder = '#';
    input.style.width = '70px'; // Make inputs narrower for better mobile experience
    input.setAttribute('data-match-score-target', 'gameScoreInput');

    // Set tabindex to create a vertical flow for each game
    // Game 1 side A = 1, Game 1 side B = 2, Game 2 side A = 3, etc.
    const tabIndex = (gameNumber - 1) * 2 + (side === 'a' ? 1 : 2);
    input.tabIndex = tabIndex;

    // Set default value if provided
    if (defaultValue !== null) {
      input.value = defaultValue;
    }

    // Add hidden input for game number
    if (side === 'a') {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `match[games_attributes][${gameNumber - 1}][number]`;
      hiddenInput.value = gameNumber - 1;

      const wrapper = document.createElement('div');
      wrapper.className = 'd-flex justify-content-center game-score-wrapper'; // Center the input in its cell
      wrapper.setAttribute('data-match-score-target', 'gameScoreWrapper');
      wrapper.appendChild(input);
      wrapper.appendChild(hiddenInput);
      return wrapper;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'd-flex justify-content-center game-score-wrapper'; // Center the input in its cell
    wrapper.setAttribute('data-match-score-target', 'gameScoreWrapper');
    wrapper.appendChild(input);
    return wrapper;
  }

  handlePlayerSelection() {
    const sideAName = this.getSelectedNameTextFromTarget(this.sideATarget);
    const sideBName = this.getSelectedNameTextFromTarget(this.sideBTarget);

    this.resultTarget.options.length = 1; // leave the first option and clear the rest

    // Use maxGames value if available, default to 5 (best of 5)
    const maxGames = this.hasMaxGamesValue ? this.maxGamesValue : 5;
    const minGames = Math.ceil((maxGames + 1) / 2); // Games needed to win

    let optionIndex = 1;
    for (let i = minGames; i <= maxGames; i++) {
      this.resultTarget.options[optionIndex] = new Option(
        `${sideAName} in ${i} games`,
        `A${i}`
      );
      optionIndex++;
    }
    for (let i = minGames; i <= maxGames; i++) {
      this.resultTarget.options[optionIndex] = new Option(
        `${sideBName} in ${i} games`,
        `B${i}`
      );
      optionIndex++;
    }
  }

  getSelectedNameTextFromTarget(target) {
    const selectedOption = target.options[target.selectedIndex];
    const selectedText = selectedOption.text;
    return selectedText;
  }
}
